// CreateItemDialog — shared modal for creating a new explorer folder or leaf
// row. Both flows share the same field set (parent folder + name) so they
// collapse into one component with a `kind` discriminator. The parent owns
// which mutation runs and can pre-fill the parent folder (e.g. via the
// sidebar's right-click "New … here" actions).
//
// Parent picker is a folder explorer (see FolderTreePicker) instead of
// a flat dropdown so the user gets the same hierarchy they see in the
// sidebar — easier to scan and easier to navigate when there are
// dozens of folders.

import { useEffect, useState } from 'react'

import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import FolderTreePicker from './FolderTreePicker'
import type { ExplorerItemKind } from './item-kind'
import { joinChildPath, type PathItem } from './tree'
import { readMutationError } from './mutation-error'

/** Discriminator that tells the dialog which noun to display in copy. */
export type CreateItemKind = ExplorerItemKind

const NAME_PLACEHOLDER: Record<CreateItemKind, string> = {
  folder: 'Reports',
  dashboard: 'Line 3 throughput',
  template: 'Line OEE card',
  report: 'Weekly downtime',
  doc: 'cold-start'
}

export interface CreateItemDialogProps {
  open: boolean
  kind: CreateItemKind
  /**
   * Optional override for the folder noun. Defaults to "folder" — the
   * Templates page passes "template folder" here so the title reads
   * "New template folder" instead of just "New folder".
   */
  folderNoun?: string
  /**
   * Initial parent folder path. "" means root. The user can override
   * this in the dialog by clicking another folder in the picker.
   */
  defaultParentPath: string
  /** Folder rows used to render the picker tree. */
  folders: readonly PathItem[]
  /**
   * Leaf rows — included so implicit parent folders (path slots that
   * exist only because an item references them) still show up in the
   * picker.
   */
  dashboards: readonly PathItem[]
  disabledPaths?: ReadonlySet<string>
  onClose: () => void
  /**
   * Submit handler — receives the derived `{ name, path }`. Should throw
   * on failure (the dialog surfaces the message); on success the dialog
   * resets and closes.
   */
  onSubmit: (input: { name: string; path: string }) => Promise<void>
}

const CreateItemDialog = ({
  open,
  kind,
  folderNoun = 'folder',
  defaultParentPath,
  folders,
  dashboards,
  disabledPaths,
  onClose,
  onSubmit
}: CreateItemDialogProps) => {
  const [name, setName] = useState('')
  const [parentPath, setParentPath] = useState(defaultParentPath)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset the form whenever the dialog reopens. Re-keying on
  // (open, defaultParentPath) so right-clicking different folders to
  // open the dialog pre-fills the picker correctly each time.
  useEffect(() => {
    if (open) {
      setName('')
      setParentPath(defaultParentPath)
      setError(null)
      setSubmitting(false)
    }
  }, [open, defaultParentPath])

  const previewPath = joinChildPath(parentPath, name)
  const itemNoun = kind === 'folder' ? folderNoun : kind
  const labelNoun = itemNoun.charAt(0).toUpperCase() + itemNoun.slice(1)
  const title = `New ${labelNoun}`

  const handleSubmit = async () => {
    if (previewPath === '') {
      setError('Name must contain at least one letter, digit, dash, underscore, or dot.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({ name: name.trim(), path: previewPath })
      onClose()
    } catch (err) {
      setError(readMutationError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-120">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="explorer-create-parent">Parent folder</Label>
            <FolderTreePicker
              id="explorer-create-parent"
              folders={folders}
              dashboards={dashboards}
              value={parentPath}
              onChange={setParentPath}
              disabled={submitting}
              disabledPaths={disabledPaths}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="explorer-create-name">Name</Label>
            <Input
              id="explorer-create-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={NAME_PLACEHOLDER[kind]}
              disabled={submitting}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-11 uppercase tracking-wider text-ink-3">Path</Label>
            <code className="rounded bg-bg-sunk px-2 py-1 font-mono text-12 text-ink-2">
              /{previewPath || '…'}
            </code>
            <span className="text-11 text-ink-3">
              Derived from the parent folder and the name above. Must be unique.
            </span>
          </div>
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || name.trim() === ''}>
            {submitting ? 'Creating…' : `Create ${itemNoun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateItemDialog
